import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import {
  EXPENSE_LIMIT,
  EXPENSE_STATUSES,
  useBuckets,
  useCategories,
  useExpenses,
  useRecordExpense,
  useSetExpenseLinks,
  useSetExpenseStatus,
  useVoidExpense,
  type Expense,
  type ExpenseStatus,
} from "@/api/finance";
import { useMoneyAccounts } from "@/api/cash";
import { useDriveStatus, useUploadToDrive } from "@/api/drive";
import { BooksLink } from "@/components/books/BooksLink";
import { BillsPanel } from "@/components/expenses/BillsPanel";
import { ExpenseDocs } from "@/components/expenses/ExpenseDocs";
import type { DriveUploader } from "@/components/forms/DriveLinkField";
import { ExpenseForm } from "@/components/forms/ExpenseForm";
import { ExpenseLinksForm } from "@/components/forms/ExpenseLinksForm";
import { ExpenseStatusForm } from "@/components/forms/ExpenseStatusForm";
import { ReasonForm } from "@/components/forms/ReasonForm";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";
import { SelectField, TextField } from "@/components/ui/field";
import { PageHeader } from "@/components/ui/page-header";
import { TabPanel, Tabs, pickTab, type TabItem } from "@/components/ui/tabs";
import { renewalBadge, statusBadge } from "@/lib/bills";
import { formatDate, formatInr, formatMoney } from "@/lib/format";
import { centsToAmount } from "@/lib/money";

const TABS = ["expenses", "bills"] as const;
type ExpensesTab = (typeof TABS)[number];
const TAB_ITEMS: TabItem<ExpensesTab>[] = [
  { id: "expenses", label: "Expenses" },
  { id: "bills", label: "Bills & renewals" },
];
const STATUSES: ExpenseStatus[] = ["paid", "unpaid", "to_reimburse"];

type Panel =
  | { kind: "record"; bill?: boolean }
  | { kind: "void"; expense: Expense }
  | { kind: "status"; expense: Expense }
  | { kind: "links"; expense: Expense }
  | null;

function rupees(expense: Expense): string {
  return expense.currency === "INR"
    ? formatInr(expense.amount)
    : `${formatMoney(expense.amount, expense.currency)} = ${formatInr(expense.inr_amount)}`;
}

export function ExpensesPage() {
  const [params, setParams] = useSearchParams();
  const tab = pickTab(params.get("tab"), TABS, "expenses");
  const invoiceId = params.get("invoice") ?? undefined;
  const statusParam = params.get("status");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [bucketId, setBucketId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [status, setStatus] = useState<ExpenseStatus | "">(
    STATUSES.includes(statusParam as ExpenseStatus) ? (statusParam as ExpenseStatus) : "",
  );
  const [includeVoided, setIncludeVoided] = useState(false);
  const [panel, setPanel] = useState<Panel>(null);
  const buckets = useBuckets();
  const everyBucket = useBuckets(true);
  const categories = useCategories();
  const everyCategory = useCategories(true);
  const moneyAccounts = useMoneyAccounts();
  const expenses = useExpenses({
    from: from || undefined,
    to: to || undefined,
    bucketId: bucketId || undefined,
    categoryId: categoryId || undefined,
    invoiceId,
    status: status || undefined,
    includeVoided,
  });
  const record = useRecordExpense();
  const setExpenseStatus = useSetExpenseStatus();
  const setExpenseLinks = useSetExpenseLinks();
  const voidExpense = useVoidExpense();
  const driveStatus = useDriveStatus();
  const driveUpload = useUploadToDrive();
  // Attaching is always offered; until Drive is connected it is off and points to Settings.
  const drive: DriveUploader = {
    ready: driveStatus.data?.status === "connected",
    upload: (file, kind, on, label) => driveUpload.mutateAsync({ file, kind, on, label }),
  };

  const counted = (expenses.data ?? []).filter((expense) => expense.voided_at === null);
  const spentCents = counted.reduce((sum, e) => sum + Math.round(Number(e.inr_amount) * 100), 0);

  function switchTab(next: ExpensesTab): void {
    setParams(next === "expenses" ? {} : { tab: next }, { replace: true });
    setPanel(null);
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Expenses"
        description="Money out: payouts of an invoice's split, salaries, bills and everything else, each charged to a bucket; and the recurring bills with their renewals."
        actions={
          panel === null && (
            <Button
              onClick={() => setPanel({ kind: "record", bill: tab === "bills" })}
              disabled={!buckets.data}
            >
              <Plus className="h-4 w-4" aria-hidden />
              {tab === "bills" ? "Record bill" : "Record expense"}
            </Button>
          )
        }
      />

      {panel?.kind === "record" && buckets.data && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>{panel.bill ? "Record a recurring bill" : "Record an expense"}</CardTitle>
          </CardHeader>
          <CardContent>
            <ExpenseForm
              buckets={buckets.data}
              categories={categories.data}
              accounts={moneyAccounts.data}
              drive={drive}
              initialBillingCycle={panel.bill ? "monthly" : undefined}
              submitLabel={panel.bill ? "Record bill" : "Record expense"}
              onSubmit={async (input) => {
                await record.mutateAsync(input);
                setPanel(null);
              }}
              onCancel={() => setPanel(null)}
            />
          </CardContent>
        </Card>
      )}
      {panel?.kind === "status" && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>
              {panel.expense.status === "unpaid" ? "Mark paid: " : "Mark reimbursed: "}
              {panel.expense.description}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ExpenseStatusForm
              expense={panel.expense}
              accounts={moneyAccounts.data}
              onSubmit={async (input) => {
                await setExpenseStatus.mutateAsync({
                  id: panel.expense.id,
                  input,
                  version: panel.expense.version,
                });
                setPanel(null);
              }}
              onCancel={() => setPanel(null)}
              onReload={() => {
                void expenses.refetch();
                setPanel(null);
              }}
            />
          </CardContent>
        </Card>
      )}
      {panel?.kind === "links" && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>Bill and proof: {panel.expense.description}</CardTitle>
          </CardHeader>
          <CardContent>
            <ExpenseLinksForm
              expense={panel.expense}
              drive={drive}
              onSubmit={async (input) => {
                await setExpenseLinks.mutateAsync({
                  id: panel.expense.id,
                  input,
                  version: panel.expense.version,
                });
                setPanel(null);
              }}
              onCancel={() => setPanel(null)}
              onReload={() => {
                void expenses.refetch();
                setPanel(null);
              }}
            />
          </CardContent>
        </Card>
      )}
      {panel?.kind === "void" && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>Void {panel.expense.description}</CardTitle>
          </CardHeader>
          <CardContent>
            <ReasonForm
              label="Why is it being voided?"
              hint="The expense stays on record, marked voided, and stops counting."
              submitLabel="Void expense"
              submitVariant="destructive"
              onSubmit={async (reason) => {
                await voidExpense.mutateAsync({
                  id: panel.expense.id,
                  reason,
                  version: panel.expense.version,
                });
                setPanel(null);
              }}
              onCancel={() => setPanel(null)}
              onReload={() => {
                void expenses.refetch();
                setPanel(null);
              }}
            />
          </CardContent>
        </Card>
      )}

      <div>
        <Tabs label="Expenses" items={TAB_ITEMS} value={tab} onChange={switchTab} />
        <TabPanel id={tab}>
          {tab === "bills" ? (
            <BillsPanel
              onRecord={() => setPanel({ kind: "record", bill: true })}
              onMarkPaid={(expense) => setPanel({ kind: "status", expense })}
              onEditLinks={(expense) => setPanel({ kind: "links", expense })}
            />
          ) : (
            <div className="flex flex-col gap-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[auto_auto_minmax(0,12rem)_minmax(0,14rem)_minmax(0,12rem)] lg:items-end lg:gap-4">
                <TextField id="expenses-from" label="From" type="date" value={from} onChange={setFrom} />
                <TextField id="expenses-to" label="To" type="date" value={to} onChange={setTo} />
                <SelectField
                  id="expenses-status"
                  label="Status"
                  value={status}
                  onChange={(value) => setStatus(value as ExpenseStatus | "")}
                  placeholder="Any status"
                  options={EXPENSE_STATUSES.map((s) => ({
                    value: s.value,
                    label: s.value === "paid" ? "Paid" : s.value === "unpaid" ? "Unpaid" : "To reimburse",
                  }))}
                />
                <SelectField
                  id="expenses-category"
                  label="Category"
                  value={categoryId}
                  onChange={setCategoryId}
                  placeholder="All categories"
                  options={(everyCategory.data ?? []).map((category) => ({
                    value: category.id,
                    label: category.archived ? `${category.name} (archived)` : category.name,
                  }))}
                />
                <SelectField
                  id="expenses-bucket"
                  label="Bucket"
                  value={bucketId}
                  onChange={setBucketId}
                  placeholder="All buckets"
                  options={(everyBucket.data ?? []).map((bucket) => ({
                    value: bucket.id,
                    label: bucket.archived ? `${bucket.name} (archived)` : bucket.name,
                  }))}
                />
              </div>
              <Checkbox
                id="expenses-voided"
                label="Show voided expenses"
                checked={includeVoided}
                onChange={setIncludeVoided}
              />
              {invoiceId && (
                <p className="text-sm">
                  Showing one invoice's payouts.{" "}
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => setParams({}, { replace: true })}
                  >
                    Show all expenses
                  </button>
                </p>
              )}

              {expenses.isPending ? (
                <p className="text-muted-foreground">Loading expenses…</p>
              ) : expenses.isError ? (
                <RequestError error={expenses.error} />
              ) : expenses.data.length === 0 ? (
                <EmptyState
                  title="No expenses here"
                  description="Record what the company pays out. A planner's payout is recorded from the invoice it comes from."
                />
              ) : (
                <>
                  <p className="text-sm">
                    Spent <strong>{formatInr(centsToAmount(spentCents))}</strong> across the{" "}
                    {counted.length === 1 ? "expense" : `${counted.length} expenses`} listed
                    {expenses.data.length === EXPENSE_LIMIT && (
                      <span className="text-muted-foreground">
                        {" "}
                        (the newest {EXPENSE_LIMIT} - narrow the dates for the rest)
                      </span>
                    )}
                    {counted.length !== expenses.data.length && (
                      <span className="text-muted-foreground"> · voided ones not counted</span>
                    )}
                  </p>
                  <ul className="flex flex-col gap-3 md:hidden">
                    {expenses.data.map((expense) => {
                      const flag = statusBadge(expense);
                      return (
                        <li
                          key={expense.id}
                          className="flex items-start justify-between gap-3 rounded-lg border bg-card p-4 shadow-sm"
                        >
                          <div className="min-w-0 text-sm">
                            <p className="font-semibold">{rupees(expense)}</p>
                            <p>
                              {expense.description}
                              {expense.voided_at && (
                                <Badge variant="destructive" className="ml-2">
                                  Voided
                                </Badge>
                              )}
                              {!expense.voided_at && flag && (
                                <Badge variant={flag.tone} className="ml-2">
                                  {flag.label}
                                </Badge>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {[
                                formatDate(expense.spent_on),
                                expense.category_name,
                                expense.bucket_name,
                                expense.invoice_no,
                                expense.payee,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                            <ExpenseDocs expense={expense} />
                            {!expense.voided_at && <BooksLink kind="expense" id={expense.id} />}
                          </div>
                          {!expense.voided_at && panel === null && (
                            <div className="flex flex-col items-end gap-1">
                              {flag && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setPanel({ kind: "status", expense })}
                                >
                                  {expense.status === "unpaid" ? "Mark paid" : "Reimbursed"}
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setPanel({ kind: "links", expense })}
                              >
                                Bill & proof
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setPanel({ kind: "void", expense })}
                              >
                                Void
                              </Button>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  <div className="hidden table-frame md:block">
                    <table className="data-table">
                      <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className="px-4 py-2 font-medium">Date</th>
                          <th className="px-4 py-2 font-medium">What for</th>
                          <th className="px-4 py-2 font-medium">Category</th>
                          <th className="px-4 py-2 font-medium">Status</th>
                          <th className="px-4 py-2 font-medium">Invoice</th>
                          <th className="px-4 py-2 text-right font-medium">Amount</th>
                          <th className="px-4 py-2">
                            <span className="sr-only">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {expenses.data.map((expense) => {
                          const flag = statusBadge(expense);
                          const renewal = renewalBadge(expense);
                          return (
                            <tr key={expense.id} className="border-b last:border-0">
                              <td className="whitespace-nowrap px-4 py-2">
                                {formatDate(expense.spent_on)}
                                {expense.paid_on && (
                                  <span className="block text-xs text-muted-foreground">
                                    paid {formatDate(expense.paid_on)}
                                  </span>
                                )}
                                {expense.reimbursed_on && (
                                  <span className="block text-xs text-muted-foreground">
                                    repaid {formatDate(expense.reimbursed_on)}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-2">
                                <span className="font-medium">{expense.description}</span>
                                {expense.voided_at && (
                                  <Badge variant="destructive" className="ml-2">
                                    Voided
                                  </Badge>
                                )}
                                {renewal && (
                                  <Badge variant={renewal.tone} className="ml-2">
                                    {renewal.label}
                                  </Badge>
                                )}
                                <span className="block text-xs text-muted-foreground">
                                  {[expense.payee, expense.reference, expense.void_reason]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </span>
                                <ExpenseDocs expense={expense} />
                                {!expense.voided_at && <BooksLink kind="expense" id={expense.id} />}
                              </td>
                              <td className="px-4 py-2">
                                {expense.category_name ?? (expense.invoice_id ? "Payout" : "—")}
                                <span className="block text-xs text-muted-foreground">
                                  {expense.bucket_name}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-4 py-2">
                                {flag ? <Badge variant={flag.tone}>{flag.label}</Badge> : "Paid"}
                              </td>
                              <td className="whitespace-nowrap px-4 py-2">
                                {expense.invoice_id ? (
                                  <Link
                                    to={`/invoices/${expense.invoice_id}`}
                                    className="text-primary hover:underline"
                                  >
                                    {expense.invoice_no}
                                  </Link>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                                {rupees(expense)}
                              </td>
                              <td className="whitespace-nowrap px-4 py-2 text-right">
                                {!expense.voided_at && panel === null && (
                                  <>
                                    {flag && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setPanel({ kind: "status", expense })}
                                      >
                                        {expense.status === "unpaid" ? "Mark paid" : "Mark reimbursed"}
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setPanel({ kind: "links", expense })}
                                    >
                                      Bill & proof
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setPanel({ kind: "void", expense })}
                                    >
                                      Void
                                    </Button>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </TabPanel>
      </div>
    </div>
  );
}
